import React, { useState } from "react";
import { Rate } from "antd";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useGetEmployeeRemarksQuery, useAddEmployeeRemarkMutation } from "../employeesApi";
import { useCanAddRemarks } from "../useEmployeePermissions";

// Fixed 4-value scale (Batch52, decided with the user via AskUserQuestion) -
// star position maps 1:1 onto the enum the API stores, worst to best, so a
// higher star count always reads as a better grade. antd's Rate already
// treats clicking the currently-selected star as a clear (0), which is
// exactly the "grade is optional per entry" behaviour the API allows.
//
// MUST stay UPPERCASE - this is the API's actual wire contract (confirmed by
// reading EmployeeMasterService.cs's own `ValidGrades = { "EXCELLENT",
// "GOOD", "AVERAGE", "BAD" }`, matched with a case-sensitive Array.Contains,
// and Batch52's SQL CHECK constraint uses the same uppercase literals).
const GRADE_STEPS = ["BAD", "AVERAGE", "GOOD", "EXCELLENT"];
export const starsToGrade = (stars) => (stars > 0 ? GRADE_STEPS[stars - 1] : null);
export const gradeToStars = (grade) => {
  const i = GRADE_STEPS.indexOf(grade);
  return i >= 0 ? i + 1 : 0;
};
export const formatGrade = (grade) => (grade ? grade.charAt(0) + grade.slice(1).toLowerCase() : "No Grade");
// No grade reads the same as Bad (red) - a note with nothing to show for it
// is just as worth a supervisor's attention as a bad one, per direct
// instruction (2026-08-15).
const GRADE_BADGE_CLASS = { EXCELLENT: "badge-linesuccess", GOOD: "badge-lineinfo", AVERAGE: "badge-linewarning", BAD: "badge-linedanger" };
export const gradeBadgeClass = (grade) => GRADE_BADGE_CLASS[grade] || "badge-linedanger";

// "Medium" star size - antd's own default (20px) reads small once dropped
// into a card full of other controls; this sits between that default and a
// showy hero size.
const starStyle = { fontSize: 26 };

export const GradeStars = ({ grade, size = starStyle }) => (
  <div className="d-flex align-items-center gap-2">
    <Rate disabled value={gradeToStars(grade)} count={4} style={size} />
    <span className={`badge ${gradeBadgeClass(grade)}`}>{formatGrade(grade)}</span>
  </div>
);

// The identity header + Current Grade + Add Remark form from Guard Remarks &
// Grading (EmployeeRemarksPage), factored out (2026-09-05) so Guard Lookup's
// own quick-grade popup can reuse the exact same form/validation/mutation
// instead of duplicating it - `showTimeline` lets EmployeeRemarksPage keep
// its full history list while Guard Lookup's compact popup opts out of it
// (direct instruction: "only the top section... as a small popup").
// `employee` is the minimal shape both callers already have on hand:
// { recordGuid, empName, empLastName, empCode, branchCode }.
const GuardGradingPanel = ({ employee, showTimeline = true }) => {
  const canAdd = useCanAddRemarks();
  const [stars, setStars] = useState(0);
  const [noteText, setNoteText] = useState("");

  const {
    data: remarks = [],
    isLoading: isLoadingRemarks,
    isError: isRemarksError,
    error: remarksError,
  } = useGetEmployeeRemarksQuery(employee?.recordGuid, { skip: !employee });
  const [addRemark, { isLoading: isSaving }] = useAddEmployeeRemarkMutation();

  const currentGrade = remarks[0]?.currentGrade || null;

  const handleAddRemark = async () => {
    if (!employee) return;
    if (!noteText.trim()) {
      toast.error("Enter a remark");
      return;
    }
    try {
      await addRemark({ recordGuid: employee.recordGuid, grade: starsToGrade(stars), remarks: noteText.trim() }).unwrap();
      toast.success("Remark added");
      setStars(0);
      setNoteText("");
    } catch (error) {
      toast.error(error?.message || "Could not add remark");
    }
  };

  if (!employee) return null;

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 className="mb-0">
            {employee.empName} {employee.empLastName}
          </h5>
          <span className="text-muted small">
            Code {employee.empCode} · {employee.branchCode}
          </span>
        </div>
        <div className="text-end">
          <div className="text-muted small mb-1">Current Grade</div>
          <GradeStars grade={currentGrade} size={{ fontSize: 20 }} />
        </div>
      </div>

      {canAdd && (
        <div className="border rounded p-3 mb-4">
          <h6 className="mb-2">Add Remark</h6>
          <div className="mb-2">
            <label className="form-label d-block">Grade (optional)</label>
            <Rate value={stars} onChange={setStars} count={4} style={starStyle} />
            <span className="ms-2 text-muted small">{formatGrade(starsToGrade(stars))}</span>
          </div>
          <div className="mb-2">
            <label className="form-label">Remark</label>
            <textarea
              className="form-control"
              rows={3}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="e.g. Involved in theft at site, or a commendation for good conduct"
            />
          </div>
          <button type="button" className="btn btn-primary" onClick={handleAddRemark} disabled={isSaving}>
            Save Remark
          </button>
        </div>
      )}

      {showTimeline && (
        <>
          <h6 className="mb-2">Timeline</h6>
          {isLoadingRemarks && <p className="text-muted small">Loading...</p>}
          {isRemarksError && (
            <div className="alert alert-danger py-2 px-3">
              Could not load this guard's remarks ({remarksError?.message || "server error"}).
            </div>
          )}
          {!isLoadingRemarks && !isRemarksError && remarks.length === 0 && (
            <p className="text-muted small">No remarks logged yet.</p>
          )}
          <div className="d-flex flex-column gap-3">
            {remarks.map((r) => (
              <div key={r.recordGuid} className="border-start border-3 border-primary ps-3">
                <div className="d-flex justify-content-between align-items-center">
                  <GradeStars grade={r.grade} size={{ fontSize: 18 }} />
                  <span className="text-muted small">{dayjs(r.createdDate).format("DD MMM YYYY, HH:mm")}</span>
                </div>
                <p className="mb-1 mt-1">{r.remarks}</p>
                <span className="text-muted small">by {r.createdByName || "-"}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default GuardGradingPanel;
